import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Row,
  Col,
  Input,
  Button,
  Space,
  Select,
  Table,
  Modal,
  Form,
  DatePicker,
  Tooltip,
  Typography,
} from "antd";
import axios from "axios";
import { useDispatch } from "react-redux";
import moment from "moment";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  showLoading,
  hideLoading,
} from "../../redux/alertsSlice";
import Layout from "../../components/layout";
import { ruleToMatcher } from "../../components/utils/clinicDay";

const { Text } = Typography;

/* —————————————————————————————————— */
/* Helpers                                                               */
const getStatusColor = (s) =>
  ({
    approved: "approved-status",
    rejected: "rejected-status",
    "follow-up": "followup-status",
    completed: "completed-status",
  }[s] || "pending-status");

/* —————————————————————————————————— */
/* Component                                                              */
function DoctorAppointments() {
  const dispatch = useDispatch();

  /* ───────────────── state ───────────────── */
  const [appointments, setAppointments] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  /* modal state */
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionStatus, setActionStatus] = useState("");
  const [form] = Form.useForm();

  /* ───────────────── data fetch ───────────────── */
  const getAppointmentData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const { data } = await axios.get(
        "/api/doctor/get-appointments-by-doctor-id",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      dispatch(hideLoading());
      if (data.success) setAppointments(data.data);
    } catch (err) {
      dispatch(hideLoading());
      console.error(err);
    }
  }, [dispatch]);

  useEffect(() => {
    getAppointmentData();
  }, [getAppointmentData]);

  /* ───────────────── filters & search ───────────────── */
  const filteredData = appointments.filter((a) => {
    const name = a.userInfo?.name?.toLowerCase() || "";
    const matchesSearch = name.includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  /* ───────────────── export helpers ───────────────── */
  const exportToCSV = () => {
    const data = filteredData.map((a) => ({
      Patient: a.userInfo?.name || "N/A",
      Service: a.doctorInfo?.service || "N/A",
      "Clinic Day": a.doctorInfo?.clinicDay?.join(", ") || "N/A",
      "Scheduled Date": a.schedule_date
        ? moment(a.schedule_date).format("DD-MM-YYYY")
        : "Not set",
      Status: a.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Appointments");
    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      "doctor-appointments.xlsx"
    );
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [
        [
          "Patient",
          "Service",
          "Clinic Day",
          "Scheduled Date",
          "Status",
        ],
      ],
      body: filteredData.map((a) => [
        a.userInfo?.name || "N/A",
        a.doctorInfo?.service || "N/A",
        a.doctorInfo?.clinicDay?.join(", ") || "N/A",
        a.schedule_date
          ? moment(a.schedule_date).format("DD-MM-YYYY")
          : "Not set",
        a.status,
      ]),
    });
    doc.save("doctor-appointments.pdf");
  };

  /* ───────────────── modal helpers ───────────────── */
  const showModal = (record, status) => {
    setSelectedRecord(record);
    setActionStatus(status);
    form.resetFields();
    setOpen(true);
  };

  const handleOk = async () => {
    try {
      const { comment, schedule_date } = await form.validateFields();
      if (actionStatus === "approved" && !schedule_date) {
        toast.error("Please pick a schedule date");
        return;
      }

      setConfirmLoading(true);
      dispatch(showLoading());

      await axios.post(
        "/api/doctor/change-appointment-status",
        {
          appointmentId: selectedRecord._id,
          status: actionStatus,
          comment,
          schedule_date,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(`Appointment ${actionStatus}`);
      setOpen(false);
      form.resetFields();
      getAppointmentData();
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setConfirmLoading(false);
      dispatch(hideLoading());
    }
  };

  /* ───────────────── clinic-day helpers ───────────────── */
  const clinicMatcher = useMemo(() => {
    const rules = selectedRecord?.doctorInfo?.clinicDay || [];
    const matchers = rules.map(ruleToMatcher);
    return (d) => matchers.some((fn) => fn(d));
  }, [selectedRecord]);

  const disableNonClinicDays = (current) =>
    !current ||
    !current.isValid() ||
    current < moment().startOf("day") ||
    !clinicMatcher(current);

  const nextClinicDate = useMemo(() => {
    let d = moment().startOf("day");
    for (let i = 0; i < 30; i++) {
      if (clinicMatcher(d)) return d;
      d.add(1, "day");
    }
    return null;
  }, [clinicMatcher]);

  /* ───────────────── columns ───────────────── */
  const columns = [
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => r.userInfo?.name || "N/A",
      sorter: (a, b) =>
        (a.userInfo?.name || "").localeCompare(
          b.userInfo?.name || ""
        ),
      sortOrder:
        sortedInfo.columnKey === "patient" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Service",
      key: "service",
      render: (_, r) => r.doctorInfo?.service || "N/A",
      filters: [
        ...new Set(
          appointments.map((a) => a.doctorInfo?.service)
        ),
      ].map((s) => ({ text: s, value: s })),
      filteredValue: filteredInfo.service || null,
      onFilter: (v, r) => r.doctorInfo?.service === v,
      sorter: (a, b) =>
        (a.doctorInfo?.service || "").localeCompare(
          b.doctorInfo?.service || ""
        ),
      sortOrder:
        sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Clinic Day",
      key: "clinic",
      render: (_, r) =>
        r.doctorInfo?.clinicDay?.join(", ") || "N/A",
      ellipsis: true,
    },
    {
      title: "Scheduled Date",
      key: "schedule_date",
      render: (_, r) =>
        r.schedule_date
          ? moment(r.schedule_date).format("DD-MM-YYYY")
          : "Not set",
      sorter: (a, b) =>
        moment(a.schedule_date || 0).unix() -
        moment(b.schedule_date || 0).unix(),
      sortOrder:
        sortedInfo.columnKey === "schedule_date"
          ? sortedInfo.order
          : null,
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => (
        <span
          className={`status-indicator ${getStatusColor(r.status)}`}
        >
          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
        </span>
      ),
    },

    {
      title: "Action",
      key: "action",
      render: (_, r) =>
        r.status === "pending" && (
          <Space>
            <Button type="link" onClick={() => showModal(r, "approved")}>
              Approve
            </Button>
            <Button danger onClick={() => showModal(r, "rejected")}>
              Reject
            </Button>
          </Space>
        ),
    },
  ];

  /* ───────────────── render ───────────────── */
  return (
    <Layout>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Input
            placeholder="Search patient..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col xs={24} md={6}>
          <Select
            style={{ width: "100%" }}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={[
              { value: "All", label: "All Statuses" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
              { value: "follow-up", label: "Follow-up" },
              { value: "completed", label: "Completed" },
            ]}
          />
        </Col>
        <Col xs={24} md={12}>
          <Space wrap>
            <Button onClick={exportToCSV}>Export Excel</Button>
            <Button onClick={exportToPDF}>Export PDF</Button>
            <Button onClick={() => setFilteredInfo({})}>Clear Filters</Button>
            <Button
              onClick={() => {
                setFilteredInfo({});
                setSortedInfo({});
              }}
            >
              Reset
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredData.map((a) => ({ ...a, key: a._id }))}
        onChange={(_, f, s) => {
          setFilteredInfo(f);
          setSortedInfo(s);
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={
          actionStatus === "approved"
            ? "Approve Appointment"
            : "Reject Appointment"
        }
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        okText={actionStatus === "approved" ? "Approve" : "Reject"}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Comment"
            name="comment"
            rules={[
              { required: true, message: "Please add a comment" },
            ]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          {actionStatus === "approved" && (
            <Form.Item
              label="Schedule date"
              name="schedule_date"
              rules={[
                {
                  required: true,
                  message: "Please select a schedule date",
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={disableNonClinicDays}
              />
            </Form.Item>
          )}

          {nextClinicDate && (
            <Text type="secondary">
              Next clinic day:&nbsp;
              <Tooltip title={nextClinicDate.calendar()}>
                {nextClinicDate.format("dddd, MMMM D, YYYY")}
              </Tooltip>
            </Text>
          )}
        </Form>
      </Modal>
    </Layout>
  );
}

export default DoctorAppointments;
